import log from '@/lib/logger';
import type { AppDispatch } from '@/store';
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import { http } from 'config';
import { showSnackbarMessage } from 'features/Common';
import Nprogress from 'nprogress';

interface AuthState {
  logging: boolean;
  fetching: boolean;
  tokenIsValid: boolean | undefined;
  resetRequestSent: boolean;
  passwordConfirmed: boolean;
}

// ----------------------------------- Actions ---------------------------------
// Action Types
export const RESET_PASSWORD_START = 'auth/reset_password_start';
export const RESET_PASSWORD_END = 'auth/reset_password_end';
export const VALIDATE_TOKEN_START = 'auth/validate_token_start';
export const VALIDATE_TOKEN_END = 'auth/validate_token_end';
export const CONFIRM_PASSWORD_START = 'auth/confirm_password_start';
export const CONFIRM_PASSWORD_END = 'auth/confirm_password_end';
export const LOGIN_START = 'auth/login_start';
export const LOGIN_END = 'auth/login_end';
export const USER_FETCH_START = 'auth/user_fetch_start';
export const USER_FETCH_END = 'auth/user_fetch_end';

// Action Creators
export const resetPasswordStart = () => ({ type: RESET_PASSWORD_START });
export const resetPasswordEnd = (sent: boolean) => ({
  type: RESET_PASSWORD_END,
  payload: sent,
});
export const validateTokenStart = () => ({ type: VALIDATE_TOKEN_START });
export const validateTokenEnd = (tokenIsValid: boolean) => ({
  type: VALIDATE_TOKEN_END,
  payload: tokenIsValid,
});
export const confirmPasswordStart = () => ({ type: CONFIRM_PASSWORD_START });
export const confirmPasswordEnd = (value: boolean) => ({ type: CONFIRM_PASSWORD_END });
export const loginStart = () => ({ type: LOGIN_START });
export const loginEnd = () => ({ type: LOGIN_END });
export const userFetchStart = () => ({ type: USER_FETCH_START });
export const userFetchEnd = () => ({ type: USER_FETCH_END });

// ----------------------------------- Reducer ---------------------------------
// Init State
const INIT_STATE: AuthState = {
  logging: false,
  fetching: false,
  tokenIsValid: undefined,
  resetRequestSent: false,
  passwordConfirmed: false,
};

// Reducer
const authReducer = (state = INIT_STATE, action: UnknownAction): AuthState => {
  const { type, payload } = action as PayloadAction<boolean>;
  switch (type) {
    case LOGIN_START: {
      return { ...state, logging: true };
    }
    case LOGIN_END: {
      return { ...state, logging: false };
    }
    case USER_FETCH_START: {
      return { ...state, fetching: true };
    }
    case USER_FETCH_END: {
      return { ...state, fetching: false };
    }
    case RESET_PASSWORD_START: {
      return { ...state, logging: true };
    }
    case RESET_PASSWORD_END: {
      return { ...state, logging: false, resetRequestSent: payload };
    }
    case VALIDATE_TOKEN_START: {
      return { ...state, logging: true };
    }
    case VALIDATE_TOKEN_END: {
      return { ...state, logging: false, tokenIsValid: payload };
    }
    case CONFIRM_PASSWORD_START: {
      return { ...state, logging: true };
    }
    case CONFIRM_PASSWORD_END: {
      return { ...state, logging: false, passwordConfirmed: payload };
    }
    default:
      return state;
  }
};

export default authReducer;

// ----------------------------------- Side Effects ----------------------------
// Thunks

export const sendResetPasswordRequest = (email: string) => (dispatch: AppDispatch) => {
  dispatch(resetPasswordStart());
  http.defaults.headers.common.Authorization = undefined;
  document.cookie = 'sessionid=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
  Nprogress.start();
  return http
    .post('accounts/password_reset/', { email })
    .then(({ data }) => {
      dispatch(showSnackbarMessage('Email enviado exitosamente.'));
      dispatch(resetPasswordEnd(true));
      Nprogress.done();
    })
    .catch((error) => {
      dispatch(resetPasswordEnd(false));
      if (error.response && error.response.data.email)
        dispatch(showSnackbarMessage(error.response.data.email));
      log.error(error);
      Nprogress.done();
    });
};

export const validateToken = (token: string) => (dispatch: AppDispatch) => {
  dispatch(validateTokenStart());
  http.defaults.headers.common.Authorization = undefined;
  document.cookie = 'sessionid=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
  Nprogress.start();
  return http
    .post('accounts/password_reset/validate_token/', { token })
    .then(({ data }) => {
      dispatch(showSnackbarMessage('Link válido. Sigue las instrucciones para finalizar.'));
      dispatch(validateTokenEnd(true));
      Nprogress.done();
    })
    .catch((error) => {
      dispatch(showSnackbarMessage('Link expirado. Inicia el proceso nuevamente.'));
      dispatch(validateTokenEnd(false));
      log.error(error);
      Nprogress.done();
    });
};

export const confirmPassword = (password: string, token: string) => (dispatch: AppDispatch) => {
  dispatch(confirmPasswordStart());
  document.cookie = 'sessionid=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
  http.defaults.headers.common.Authorization = undefined;
  Nprogress.start();
  return http
    .post('accounts/password_reset/confirm/', { password, token })
    .then(({ data }) => {
      dispatch(showSnackbarMessage('Contraseña actualizada exitosamente'));
      dispatch(confirmPasswordEnd(true));
      Nprogress.done();
    })
    .catch((error) => {
      dispatch(showSnackbarMessage('Error de conexión. Intente nuevamente.'));
      dispatch(confirmPasswordEnd(false));
      log.error(error);
      Nprogress.done();
    });
};
